import React, { useEffect } from "react";
import {
  Breadcrumbs, Button, Checkbox, PageHeader, Select, TextInput, Tooltip,
} from "@opendate-app/setlist";

import { listHref } from "../shared/listUrlState";
import { useBroadcastNewContext } from "./BroadcastNewContainer";
import BroadcastTypeCard, { EmailIllustration, PushIllustration, SmsIllustration } from "./BroadcastTypeCard";

const BROADCAST_TYPES = [
  { value: "email", label: "Email", description: "Announce upcoming events or news.", Illustration: EmailIllustration },
  { value: "sms", label: "SMS", description: "Send important text updates to fans.", Illustration: SmsIllustration },
  {
    value: "push", label: "Push", description: "Alert fans with mobile app notifications.", Illustration: PushIllustration, disabled: true,
  },
];

const styles = {
  card: {
    marginTop: 24,
    background: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--radius-xl)",
  },
  body: {
    display: "flex", flexDirection: "column", gap: 20, padding: 24,
  },
  fields: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 },
  sectionLabel: {
    display: "block",
    marginBottom: 6, fontSize: 14, lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-secondary)",
  },
  types: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 },
  tooltipFill: { width: "100%" },
  help: {
    margin: "4px 0 0 32px", fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary)",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: "16px 24px",
    borderTop: "1px solid var(--sl-border-secondary)",
  },
};

const BroadcastNew = () => {
  const {
    team,
    venueOwnerships,
    selectedVenueOwnership,
    setSelectedVenueOwnership,
    name,
    setName,
    broadcastType,
    setBroadcastType,
    transactional,
    setTransactional,
    createBroadcast,
  } = useBroadcastNewContext();

  const selectedType = broadcastType === "push" ? "email" : broadcastType;

  useEffect(() => {
    if (broadcastType === "push") setBroadcastType("email");
  }, []);

  const workspaceOptions = (venueOwnerships || []).map((venueOwnership) => ({
    value: venueOwnership.id,
    label: venueOwnership.venueable?.name || venueOwnership.name,
  }));

  return (
    <div>
      <PageHeader
        breadcrumbs={(
          <Breadcrumbs
            items={[
              { label: "Broadcasts", href: listHref(`/teams/${team.id}/broadcasts`) },
              { label: "New broadcast" },
            ]}
          />
        )}
        title="New broadcast"
        supportingText="Broadcasts are sent to multiple people at once, at a time you specify."
      />

      <form style={styles.card} onSubmit={(event) => event.preventDefault()}>
        <div style={styles.body}>
          <div style={styles.fields}>
            <div>
              <label htmlFor="broadcast-name" style={styles.sectionLabel}>Name</label>
              <TextInput
                id="broadcast-name"
                placeholder="e.g. Fall lineup announcement"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div>
              <div id="broadcast-workspace-label" style={styles.sectionLabel}>Workspace</div>
              <Select
                aria-labelledby="broadcast-workspace-label"
                options={workspaceOptions}
                value={selectedVenueOwnership?.id}
                onChange={(id) => setSelectedVenueOwnership(venueOwnerships.find((venueOwnership) => venueOwnership.id === id))}
              />
            </div>
          </div>

          <div>
            <div id="broadcast-type-label" style={styles.sectionLabel}>Broadcast type</div>
            <div role="radiogroup" aria-labelledby="broadcast-type-label" style={styles.types}>
              {BROADCAST_TYPES.map((type) => {
                const card = (
                  <BroadcastTypeCard
                    key={type.value}
                    value={type.value}
                    label={type.label}
                    description={type.description}
                    Illustration={type.Illustration}
                    selected={!type.disabled && selectedType === type.value}
                    disabled={Boolean(type.disabled)}
                    onSelect={setBroadcastType}
                  />
                );

                return type.disabled
                  ? <Tooltip key={type.value} title="Coming soon"><div style={styles.tooltipFill}>{card}</div></Tooltip>
                  : card;
              })}
            </div>
          </div>

          <div>
            <Checkbox
              label="Transactional message"
              checked={transactional}
              onChange={(event) => setTransactional(event.target.checked)}
            />
            <p style={styles.help}>
              Sent to both subscribed and unsubscribed fans. Make sure to follow{" "}
              <a href="https://en.wikipedia.org/wiki/Email_spam_legislation_by_country" target="_blank" rel="noopener noreferrer">
                local laws
              </a>{" "}
              about sending to unsubscribed people.
            </p>
          </div>
        </div>

        <div style={styles.footer}>
          <Button variant="primary" onClick={createBroadcast}>Create</Button>
        </div>
      </form>
    </div>
  );
};

export default BroadcastNew;
